import { ReactNode } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import { actions } from 'store';
import { breakpoints } from 'styles';
import { ReaderSidebarContent, ReaderSidebarHeader } from '.';

import type { ReaderTheme } from 'shared-types';

interface Props {
  backgroundImage?: string;
  children: ReactNode;
  dropdownContent?: ReactNode;
  id: string;
  left?: boolean;
  onClose?: () => void;
  title: string;
  visible?: boolean;
}

const ReaderSidebar = ({
  backgroundImage,
  children,
  dropdownContent,
  id,
  left,
  onClose,
  title,
  visible
}: Props) => {
  const { dispatch } = useDispatch();
  const {
    pageState: { readerTheme = {} as ReaderTheme, sidebarHeight },
    theme
  } = useReduxState();

  const handleClose = () => {
    if (onClose) onClose();
    else
      dispatch(
        actions.updatePageState({
          detailedFeedback: false,
          sidebarVisible: ''
        })
      );
  };

  return (
    <div className="wrapper">
      <div
        className={`sidebar ${left ? 'left' : 'right'} ${
          visible ? 'visible' : ''
        }`}
        id={id}
        style={{
          backgroundColor: readerTheme.sidebarBackgroundColor,
          ...(backgroundImage
            ? {
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: 'cover'
              }
            : {}),
          height: sidebarHeight
        }}
      >
        {title && visible ? (
          <ReaderSidebarHeader
            dropdownContent={dropdownContent}
            left={left}
            onClose={handleClose}
            title={title}
          />
        ) : null}

        <ReaderSidebarContent readerTheme={readerTheme}>
          {children}
        </ReaderSidebarContent>
      </div>

      <div
        className={`overlay ${visible ? 'visible' : ''}`}
        onClick={handleClose}
      />

      <style jsx>{`
        .sidebar {
          border-radius: ${left
            ? `0 ${theme.cardBorderRadius} ${theme.cardBorderRadius} 0`
            : `${theme.cardBorderRadius} 0 0 ${theme.cardBorderRadius}`};
          box-shadow: ${theme.cardBoxShadow};
          display: flex;
          flex-direction: column;
          top: 0;
          visibility: hidden;
          width: 0;
        }
        .sidebar.visible {
          visibility: visible;
          width: 260px;
        }
        .sidebar.left {
          left: 0;
          transform: translateX(-110%);
          transition: height 0.3s, transform 0.3s, visibility 0.1s ease 0.3s;
        }
        .sidebar.right {
          right: 0;
          transform: translateX(110%);
          transition: height 0.3s, transform 0.3s, visibility 0.1s ease 0.3s;
        }
        .sidebar.left.visible,
        .sidebar.right.visible {
          transform: none;
          transition: height 0.3s, transform 0.3s, top 0.3s;
        }
        @media (max-width: ${breakpoints.medium - 1}px) {
          .sidebar {
            position: fixed;
            bottom: 0;
            height: auto !important;
            z-index: 50;
          }
          .overlay {
            background-color: rgba(0, 0, 0, 0.5);
            display: block;
            opacity: 0;
            position: fixed;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            transition: opacity 0.3s, visibility 0.3s;
            visibility: hidden;
            z-index: 45;
          }
          .overlay.visible {
            opacity: 1;
            visibility: visible;
          }
        }
        @media (min-width: ${breakpoints.medium}px) {
          .sidebar {
            border-radius: ${theme.cardBorderRadius};
            top: 15px;
            position: absolute;
            width: 0;
          }
          .sidebar.visible {
            width: 300px;
            visibility: visible;
          }
          .sidebar.left {
            left: 15px;
          }
          .sidebar.right {
            right: 15px;
          }
          .overlay {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default ReaderSidebar;
